import "../../../css/CrawlSources.css";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Spin, Statistic, Tag, message, Row, Col, Card } from "antd";
import {
  PlayCircleOutlined,
  StopOutlined,
  HistoryOutlined,
  EditOutlined,
  SyncOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { getSource, triggerCrawl } from "../../../services/polly/crawlSourceService";
import useCrawlNotifications from "./useCrawlNotifications";
import { renderOutcome } from "./outcomeRenderers";

function formatDate(dateStr) {
  if (!dateStr) return "--";
  const d = new Date(dateStr);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function SourceDashboard() {
  const { sourceId } = useParams();
  const history = useHistory();
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [liveCounters, setLiveCounters] = useState(null); // { found, ingested, failed }
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const fetchSource = useCallback(async () => {
    try {
      const data = await getSource(sourceId);
      if (!mountedRef.current) return;
      setSource(data);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to load source:", err);
      message.error("Failed to load crawl source");
    }
    if (mountedRef.current) setLoading(false);
  }, [sourceId]);

  useEffect(() => {
    fetchSource();
  }, [fetchSource]);

  // --- WebSocket live updates ---
  const onProgress = useCallback((payload) => {
    if (String(payload.sourceId) !== String(sourceId)) return;
    setLiveCounters({
      found: payload.foundCount ?? 0,
      ingested: payload.ingestedCount ?? 0,
      failed: payload.failedCount ?? 0,
    });
    // If the server signals completion, refresh source data
    if (payload.status === "COMPLETED" || payload.status === "STOPPED") {
      setLiveCounters(null);
      fetchSource();
    }
  }, [sourceId, fetchSource]);

  const onUrlStarted = useCallback((payload) => {
    if (String(payload.sourceId) !== String(sourceId)) return;
    setLiveCounters((prev) => prev ? { ...prev } : { found: 1, ingested: 0, failed: 0 });
  }, [sourceId]);

  const onUrlFinished = useCallback((payload) => {
    if (String(payload.sourceId) !== String(sourceId)) return;
    setLiveCounters((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ingested: payload.success ? (prev.ingested + 1) : prev.ingested,
        failed: payload.success ? prev.failed : (prev.failed + 1),
      };
    });
  }, [sourceId]);

  useCrawlNotifications({ onProgress, onUrlStarted, onUrlFinished });

  // --- Actions ---
  const handleStart = async () => {
    setStarting(true);
    try {
      await triggerCrawl(sourceId);
      message.success("Crawl started");
      setLiveCounters({ found: 0, ingested: 0, failed: 0 });
      fetchSource();
    } catch (err) {
      console.warn("Failed to start crawl:", err);
      message.error("Failed to start crawl");
    }
    if (mountedRef.current) setStarting(false);
  };

  // --- Derived state ---
  const isRunning = source?.status === "CRAWLING" || liveCounters !== null;
  const hasCrawled = !!source?.lastCrawledAt;

  if (loading) {
    return (
      <div className="crawl-src-container" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!source) {
    return (
      <div className="crawl-src-container">
        <div className="crawl-src-breadcrumb">
          <a onClick={() => history.push("/polly/crawl-sources")}>Crawl Sources</a> &rsaquo; <span>Not Found</span>
        </div>
        <div className="crawl-src-body">
          <p>Crawl source not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crawl-src-container">
      <div className="crawl-src-breadcrumb">
        <a onClick={() => history.push("/polly/crawl-sources")}>Crawl Sources</a> &rsaquo; <span>{source.name}</span>
      </div>
      <div className="crawl-src-body">
        <div className="crawl-src-header">
          <h2>{source.name}</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={<EditOutlined />}
              onClick={() => history.push(`/polly/crawl-sources/${sourceId}`)}
            >
              Edit source
            </Button>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => history.push(`/polly/crawl-sources/${sourceId}/jobs`)}
            >
              Past crawls
            </Button>
          </div>
        </div>

        <div style={{ color: "#888", fontSize: 12, marginBottom: 20 }}>{source.crawlSeedUrl}</div>

        {/* --- Running state --- */}
        {isRunning && (
          <Card
            style={{ marginBottom: 20, borderColor: "#1890ff" }}
            bodyStyle={{ padding: 20 }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <Tag icon={<SyncOutlined spin />} color="blue" style={{ fontSize: 14, padding: "4px 12px" }}>
                Running
              </Tag>
            </div>
            <Row gutter={24}>
              <Col span={8}>
                <Statistic
                  title="Found"
                  value={liveCounters?.found ?? 0}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Ingested"
                  value={liveCounters?.ingested ?? 0}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Failed"
                  value={liveCounters?.failed ?? 0}
                  valueStyle={{ color: liveCounters?.failed > 0 ? "#ff4d4f" : "#888" }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* --- Idle state (has crawled before) --- */}
        {!isRunning && hasCrawled && (
          <Card style={{ marginBottom: 20 }} bodyStyle={{ padding: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: "#888", marginRight: 8 }}>Last crawled:</span>
              <span>{formatDate(source.lastCrawledAt)}</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: "#888", marginRight: 8 }}>Documents:</span>
              <span>{source.documentCount ?? "--"}</span>
            </div>
            {source.lastJob && (
              <div style={{ marginBottom: 12 }}>
                <span style={{ color: "#888", marginRight: 8 }}>Last outcome:</span>
                {renderOutcome(source.lastJob)}
              </div>
            )}
          </Card>
        )}

        {/* --- Never crawled state --- */}
        {!isRunning && !hasCrawled && (
          <Card style={{ marginBottom: 20, textAlign: "center", padding: 40 }} bodyStyle={{ padding: 40 }}>
            <p style={{ color: "#888", marginBottom: 16 }}>This source has never been crawled.</p>
          </Card>
        )}

        {/* --- Action buttons --- */}
        {!isRunning && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={starting}
            onClick={handleStart}
            size="large"
          >
            Start crawl now
          </Button>
        )}
      </div>
    </div>
  );
}
